import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdBanner = ({ position = 'top', className = '' }) => {
  const location = useLocation();
  const [adPages, setAdPages] = useState(null);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdSettings();
  }, []);

  const fetchAdSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      setAdPages(response.data.ad_pages || {
        home: true,
        search: true,
        detail: true,
        blog: true,
        contact: true,
        create_listing: false,
        edit_listing: false
      });
      setAds(response.data.ads || []);
    } catch (error) {
      console.error('Error fetching ad settings:', error);
      setAdPages({
        home: true,
        search: true,
        detail: true,
        blog: true,
        contact: true
      });
      setAds([]);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPageType = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path === '/search' || path === '/freelancers') return 'search';
    if (path.startsWith('/escort/') || path.startsWith('/freelancer/')) return 'detail';
    if (path === '/blog' || path.startsWith('/blog/')) return 'blog';
    if (path === '/contact') return 'contact';
    if (path === '/create-listing') return 'create_listing';
    if (path.startsWith('/edit-listing/')) return 'edit_listing';
    if (path.startsWith('/page/')) return 'dynamic_page';
    return 'other';
  };

  const getBanner = (pos) => {
    return ads.find(ad => ad.position === pos);
  };

  const shouldShowAd = () => {
    if (loading || !adPages) return false;
    const pageType = getCurrentPageType();
    return adPages[pageType] !== false;
  };

  if (!shouldShowAd()) {
    return null;
  }

  // Map "sidebar" to "left" for backward compatibility
  const bannerPosition = position === 'sidebar' ? 'left' : position;

  if (bannerPosition === 'top') {
    const banner = getBanner('top');
    if (!banner || !banner.active) return null;

    return (
      <div className={`w-full bg-zinc-900 border-b border-zinc-800 ${className}`}>
        <div className="container mx-auto px-4 py-2">
          {banner.image_url ? (
            <div className="flex items-center justify-center">
              {banner.link_url ? (
                <a href={banner.link_url} target="_blank" rel="noopener noreferrer">
                  <img src={banner.image_url} alt="Advertisement" className="max-h-24 md:max-h-28 rounded-lg object-contain" />
                </a>
              ) : (
                <img src={banner.image_url} alt="Advertisement" className="max-h-24 md:max-h-28 rounded-lg object-contain" />
              )}
            </div>
          ) : (
            <div className="h-24 md:h-28 bg-gradient-to-r from-zinc-800 via-zinc-700 to-zinc-800 rounded-lg flex items-center justify-center border border-zinc-700 overflow-hidden">
              <div className="text-center">
                <p className="text-zinc-400 text-sm">AD SPACE</p>
                <p className="text-zinc-500 text-xs">728x90 Banner</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (bannerPosition === 'left' || bannerPosition === 'right') {
    const banner = getBanner(bannerPosition);
    if (!banner || !banner.active) return null;

    return (
      <div className={`hidden lg:block w-40 xl:w-48 flex-shrink-0 ${className}`}>
        <div className="sticky top-24 space-y-4">
          <div className="rounded-lg overflow-hidden border border-zinc-700">
            {banner.image_url ? (
              banner.link_url ? (
                <a href={banner.link_url} target="_blank" rel="noopener noreferrer">
                  <img src={banner.image_url} alt="Advertisement" className="w-full" />
                </a>
              ) : (
                <img src={banner.image_url} alt="Advertisement" className="w-full" />
              )
            ) : (
              <div className="h-[600px] bg-gradient-to-b from-zinc-800 via-zinc-700 to-zinc-800 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-zinc-400 text-sm">AD</p>
                  <p className="text-zinc-500 text-xs">160x600</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AdBanner;
